(ns logseq.common.path
  "Path manipulation functions, use '/' sep on all platforms.
   Also handles URL paths."
  (:require [clojure.string :as string]))

(defn- safe-decode-uri-component
  [uri]
  (try
    (.normalize (js/decodeURIComponent uri) "NFC")
    (catch :default _
      (js/console.error "decode-uri-component-failed" uri)
      uri)))

(defn is-file-url?
  [s]
  (and (string? s)
       (or
        (string/starts-with? s "memory://") ;; special memory fs
        (string/starts-with? s "assets://") ;; Electron asset, urlencoded
        (string/starts-with? s "file://") ;; Electron files
        )))

(defn filename
  "File name of a path or URL.
   Returns nil when it's a directory that ends with '/'."
  [path]
  (let [fname (if (string/ends-with? path "/")
                nil
                (last (string/split path #"/")))]
    (if (and (seq fname) (is-file-url? path))
      (safe-decode-uri-component fname)
      fname)))

(defn split-ext
  "Split file name into stem and extension, for both path and URL"
  [path]
  (let [fname (filename path)
        pos (string/last-index-of fname ".")]
    (if-not (or (nil? pos) (zero? pos))
      [(subs fname 0 pos)
       (string/lower-case (subs fname (+ pos 1)))]
      [fname ""])))

(defn file-stem
  "File name without extension"
  [path]
  (first (split-ext path)))

(defn file-ext
  "File extension, lowercased"
  [path]
  (second (split-ext path)))

(defn- path-join-internal
  "Joins the given path segments into a single path, handling relative paths,
  '..' and '.' normalization."
  [& segments]
  (let [segments (remove string/blank? segments) ;; handle (path-join nil path)
        segments (map #(string/replace % #"[/\\]+" "/") segments)
        ;; a fix for clojure.string/split
        split-fn (fn [s]
                   (if (= s "/")
                     [""]
                     (string/split s #"/")))
        join-fn (fn [segs]
                  (case segs
                    []   "."
                    [""] "/"
                    #_{:clj-kondo/ignore [:path-invalid-construct/string-join]}
                    (string/join "/" segs)))]
    (->> (filter seq segments)
         (mapcat split-fn)
         (reduce (fn [acc segment]
                   (cond
                     (= "" segment)
                     [segment]

                     (= ".." segment)
                     (case (last acc)
                       ".." (conj acc segment)
                       ""   acc
                       nil  [".."]
                       (pop acc))

                     (= "." segment)
                     acc

                     :else
                     (conj acc segment)))
                 [])
         (join-fn))))

(defn- uri-path-join-internal
  "Joins the given URI path segments into a single path, handling relative paths,
  '..' and '.' normalization."
  [& segments]
  (let [segments (remove nil? segments) ;; handle (path-join nil path)
        segments (map #(string/replace % #"[/\\]+" "/") segments)
        ;; a fix for clojure.string/split
        split-fn (fn [s]
                   (if (= s "/")
                     [""]
                     (string/split s #"/")))
        join-fn (fn [segs]
                  (case segs
                    []   "."
                    [""] "/"
                    #_{:clj-kondo/ignore [:path-invalid-construct/string-join]}
                    (string/join "/" segs)))]
    (->> (filter seq segments)
         (mapcat split-fn)
         (map #(js/encodeURIComponent %))
         (reduce (fn [acc segment]
                   (cond
                     (= "" segment)
                     [segment]

                     (= ".." segment)
                     (case (last acc)
                       ".." (conj acc segment)
                       ""   acc
                       nil  [".."]
                       (pop acc))

                     (= "." segment)
                     acc

                     :else
                     (conj acc segment)))
                 [])
         (join-fn))))

(defn- preserve-file-url-win-drive
  [scheme encoded-path]
  (cond-> encoded-path
    (= scheme "file:")
    (string/replace #"^/([a-zA-Z])%3[Aa](?=/|$)" "/$1:")))

(defn url-join
  "Segments are not URL-encoded"
  [base-url & segments]
  (let [custom-scheme (re-find #"^[a-zA-Z0-9_+\-\.]+://" base-url)
        custom-scheme? (and custom-scheme (not= custom-scheme "file://"))
        base-url (cond-> base-url
                   custom-scheme? (string/replace-first custom-scheme "file://"))
        ^js url (try
                  (js/URL. (safe-decode-uri-component base-url))
                  (catch :default e
                    (js/console.error "Failed to construct URL in url-join:" base-url e)
                    (js/URL. "file:///")))
        scheme (if custom-scheme?
                 (string/replace custom-scheme "//" "")
                 (.-protocol url))
        path (.-pathname url)
        domain (or (not-empty (.-host url))
                   (if (or custom-scheme? (string/starts-with? path "/")) "" "/"))
        encoded-new-path (->> (apply uri-path-join-internal path segments)
                              (preserve-file-url-win-drive scheme))]
    (str scheme "//" domain encoded-new-path)))

(defn path-join
  "Join path segments, or URL base and path segments"
  [base & segments]
  (if (is-file-url? base)
    (apply url-join base segments)
    (let [rejoined-path (apply path-join-internal base segments)]
      (if (and (not-empty base)
               (string/starts-with? base "//")) ;; Win path fix
        (str "/" rejoined-path)
        rejoined-path))))

(defn prepend-protocol
  "Prepend protocol to path. Handle UNC path. aka. path-to-url

   protocol is one of file: http: https: assets:"
  [protocol path]
  (cond
    (string/starts-with? path protocol)
    (do
      (js/console.error "BUG: should not prepend protocol to path with protocol" protocol path)
      path)

    (string/starts-with? path "//") ;; Windows UNC path
    (str protocol path)

    :else
    (path-join (str protocol "//") path)))

(defn- path-normalize-internal
  "Normalize path using path-join, break into segment and re-join"
  [path]
  (path-join path))

(defn url-normalize
  [origin-url]
  (let [^js url (try
                  (js/URL. (safe-decode-uri-component origin-url))
                  (catch :default e
                    (js/console.error "Failed to construct URL in url-normalize:" origin-url e)
                    nil))
        scheme (when url (.-protocol url))
        domain (when url (or (not-empty (.-host url)) "/"))
        path (when url (.-pathname url))
        encoded-new-path (when url
                           (->> (uri-path-join-internal path)
                                (preserve-file-url-win-drive scheme)))]
    (if url
      (str scheme "//" domain encoded-new-path)
      origin-url)))

(defn path-normalize
  "Normalize path or URL"
  [path]
  (-> (if (is-file-url? path)
        (url-normalize path)
        (path-normalize-internal path))
      (.normalize "NFC")))

(defn url-to-path
  "Extract path part of a URL, decoded.

   The reverse operation is (path-join protocol:// path)"
  [original-url]
  (if (is-file-url? original-url)
    ;; NOTE: URL type is not consistent across all protocols
    ;; Check file:// and assets://, pathname behavior is different
    (if-let [^js url (try
                       (js/URL. (string/replace (safe-decode-uri-component original-url) "assets://" "file://"))
                       (catch :default e
                         (js/console.error "Failed to construct URL in url-to-path:" original-url e)
                         nil))]
      (let [path (.-pathname url)
            host (.-host url)
            path (if (string/starts-with? path "///")
                   (subs path 2)
                   path)
            ;; assets:// URLs protect a Windows drive colon as
            ;; /logseq__colon/ (see electron.utils/
            ;; decode-protected-assets-schema-path); restore it before the
            ;; drive-letter rule below so it resolves to a native C:/ path
            path (string/replace path "/logseq__colon/" ":/")
            path (if (re-find #"(?i)^/[a-zA-Z]:" path) ;; Win path fix
                   (subs path 1)
                   path)]
        (if (string/blank? host)
          path
          (str "//" host path)))
      original-url)
    original-url))

(defn file-url-or-path->path
  "Converts a file URL to a local filesystem path."
  [s]
  (if (is-file-url? s)
    (url-to-path s)
    s))

(defn trim-dir-prefix
  "Trim dir prefix from path"
  [base-path sub-path]
  (let [base-path (path-normalize base-path)
        sub-path (path-normalize sub-path)
        is-url? (is-file-url? base-path)]
    (if (string/starts-with? sub-path base-path)
      (if is-url?
        (safe-decode-uri-component (string/replace (subs sub-path (count base-path)) #"^/+", ""))
        (string/replace (subs sub-path (count base-path)) #"^/+", ""))
      (do
        (js/console.error "unhandled trim-base" base-path sub-path)
        nil))))

(defn parent
  "Parent, containing directory"
  [path]
  (if (string/includes? path "/")
    ;; ugly but works
    (path-normalize (str path "/.."))
    nil))

;; compat
(defn basename
  [path]
  (let [path (string/replace path #"/+$" "")]
    (filename path)))

(defn absolute?
  "Whether path `p` is absolute."
  [p]
  (let [p (path-normalize p)]
    (boolean (or (is-file-url? p)
                 (string/starts-with? p "/")
                 ;; is windows dir
                 (re-find #"^[a-zA-Z]:[/\\]" p)))))

(defn protocol-url?
  "Whether path `p` is a protocol URL.

   This is a loose check, it only checks if there is a valid protocol prefix."
  [p]
  (boolean (and (re-find #"^[a-zA-Z0-9_+\-\.]{2,}:" p) ;; HACK: avoid matching windows drive
                (not (string/includes? p " ")))))
